/**
 * MobileKnowledgeGraph — a simple single-node knowledge-graph explorer.
 *
 * CLUSTER-4 (research-visibility uplift, LOWEST priority — kept deliberately simple). The firm's KG holds
 * ~27K nodes / ~13K edges. Desktop has a full graph explorer; mobile gets a focused node-detail card: the
 * node's label + type + summary + key properties, and its high-confidence relationships (the neighbour
 * label + edge type + confidence). There is no node-search RPC, so the node is addressed by id via the
 * route param; each relationship is shown read-only (no neighbour-id is returned, so neighbours aren't yet
 * tappable — a future RPC could add neighbour ids to make the graph walkable).
 *
 * Routes:
 *   /partner/m/kg          → no node selected (calm explainer — there's no search yet)
 *   /partner/m/kg/:nodeId  → the node detail
 *
 * DATA — `mobile_kg_node(_node_id)` SECURITY DEFINER RPC, gated by can_read_research() (raising 42501
 * otherwise). It returns either
 *   { ok: true,  node: { id, node_type, label, summary, properties },
 *                relationships: [{ neighbour_label, edge_type, confidence, direction }, …] }   (edge
 *                confidence >= 0.7; LIMIT 100; confidence desc)
 *   { ok: false, reason: 'not_found' }
 *
 * READ-ONLY and offline-aware, mirroring the sibling mobile pages exactly:
 *   • ONE useCachedQuery (cacheKey scoped per node id) write-throughs the resolved payload (queryFn THROWS
 *     on { error } so the cached last-known-good keeps serving offline).
 *   • The { ok:false } not-found is a valid, non-error state → a calm "not available" card, never a banner.
 *   • The "Saved <relative time>" header hint + amber-when-offline colour mirror the sibling pages.
 *   • A non-research caller hits the 42501 gate → a graceful no-access card when nothing is cached.
 *
 * The generated supabase types are stale for this RPC, so the call is cast through `any`. No writes.
 * Web/desktop are untouched (this is a /partner/m/* surface).
 */
import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ChevronLeft, Network, ArrowRight, ArrowLeft, AlertTriangle, Share2 } from 'lucide-react';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { humanise } from '@/components/research-hub/labels';

// ---------------------------------------------------------------------------
// Shapes — exactly the keys mobile_kg_node() builds.
// ---------------------------------------------------------------------------

interface KgRelationship {
  neighbour_label: string | null;
  edge_type: string | null;
  confidence: number | null;
  direction: 'in' | 'out' | string | null;
}

interface KgNode {
  id: string;
  node_type: string | null;
  label: string | null;
  summary: string | null;
  properties: Record<string, unknown> | null;
}

interface KgNodeData {
  ok: boolean;
  node: KgNode | null;
  relationships: KgRelationship[];
}

const EMPTY_NODE: KgNodeData = { ok: false, node: null, relationships: [] };

// Postgres SQLSTATE for insufficient_privilege — the RPC raises this without research read access.
const NO_ACCESS_CODE = '42501';

function errorCode(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code?: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

async function fetchKgNode(nodeId: string): Promise<KgNodeData> {
  const { data, error } = await (supabase.rpc as any)('mobile_kg_node', { _node_id: nodeId });
  if (error) throw error;
  const raw = (data ?? {}) as {
    ok?: boolean;
    node?: KgNode | null;
    relationships?: KgRelationship[] | null;
  };
  return {
    ok: raw.ok === true,
    node: raw.node ?? null,
    relationships: Array.isArray(raw.relationships) ? raw.relationships : [],
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function confidencePct(c: number | null): string | null {
  if (typeof c !== 'number' || !Number.isFinite(c)) return null;
  return `${Math.round(c * 100)}%`;
}

// Render a property value (string/number/boolean/array/object) as a short readable string.
function propValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map((x) => propValue(x)).join(', ');
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

// Flatten the properties object into a small list of label/value pairs, skipping empty values and
// capping the number shown so the card stays a glance, not a dump.
function propertyPairs(properties: Record<string, unknown> | null, max = 8): Array<[string, string]> {
  if (!properties || typeof properties !== 'object') return [];
  const pairs: Array<[string, string]> = [];
  for (const [k, v] of Object.entries(properties)) {
    if (v == null || v === '') continue;
    const text = propValue(v).trim();
    if (!text) continue;
    pairs.push([humanise(k), text]);
    if (pairs.length >= max) break;
  }
  return pairs;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function MobileKnowledgeGraph() {
  const online = useIsOnline();
  const { nodeId = '' } = useParams<{ nodeId: string }>();

  const detail = useCachedQuery<KgNodeData>({
    queryKey: ['mobile-kg-node', nodeId],
    cacheKey: `mobile.kg_node.${nodeId}`,
    queryFn: () => fetchKgNode(nodeId),
    enabled: nodeId.length > 0,
  });

  const data = detail.data ?? EMPTY_NODE;
  const node = data.node;
  const relationships = data.relationships;

  const showStale = detail.isFromCache && detail.cachedAt != null;
  let staleLabel: string | null = null;
  if (showStale && detail.cachedAt) {
    try {
      staleLabel = `Saved ${formatDistanceToNow(new Date(detail.cachedAt), { addSuffix: true })}`;
    } catch {
      staleLabel = 'Showing saved data';
    }
  }

  const noAccess = errorCode(detail.error) === NO_ACCESS_CODE;
  const hardError = !noAccess && detail.isError && !detail.isFromCache && detail.data === undefined;
  // The RPC resolved but the node doesn't exist.
  const notFound = !hardError && !noAccess && nodeId.length > 0 && data.ok === false && !detail.isLoading;

  const properties = useMemo(() => propertyPairs(node?.properties ?? null), [node?.properties]);
  const nodeType = node?.node_type ? humanise(node.node_type) : null;

  return (
    <div className="min-h-screen bg-background px-4 pb-8 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <header className="mb-4">
        <Link
          to="/partner/m/home"
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Home
        </Link>
        <p className="text-sm text-muted-foreground">Knowledge graph</p>
        <h1 className="font-heading text-2xl font-semibold text-primary">
          {node?.label ?? (notFound ? 'Not found' : 'Entity')}
        </h1>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {nodeType && (
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">{nodeType}</p>
          )}
          {showStale && staleLabel && (
            <p
              className={cn(
                'text-[11px] font-medium uppercase tracking-wide',
                online ? 'text-muted-foreground/70' : 'text-amber-600',
              )}
            >
              {staleLabel}
            </p>
          )}
        </div>
      </header>

      <div className="space-y-3">
        {/* No node selected — there's no search yet, so explain how to reach an entity. */}
        {nodeId.length === 0 ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Network className="h-5 w-5" aria-hidden="true" />
              </span>
              <h2 className="font-heading text-sm font-semibold">Pick an entity to explore</h2>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Open a specific entity from a research item or a link to see its key facts and how it connects
              to the rest of the graph. There’s no free-text search here yet.
            </p>
          </section>
        ) : noAccess ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <h2 className="font-heading text-sm font-semibold">No access</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              You don’t have access to the knowledge graph. This view is available to the research team.
            </p>
          </section>
        ) : hardError ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">Couldn’t load</p>
            <p className="mt-2 text-sm text-muted-foreground">
              {online
                ? 'We couldn’t load this entity just now. Pull back in a moment.'
                : 'You’re offline and there’s no saved copy of this entity yet. This will fill in once you reconnect.'}
            </p>
          </section>
        ) : notFound ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <AlertTriangle className="h-5 w-5" aria-hidden="true" />
              </span>
              <h2 className="font-heading text-sm font-semibold">This entity isn’t available</h2>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              It may have been removed, or it isn’t shared with your access level.
            </p>
          </section>
        ) : node ? (
          <>
            {/* Summary lead. */}
            {node.summary && (
              <section className="rounded-xl border border-border bg-card p-4">
                <p className="text-sm text-foreground">{node.summary}</p>
              </section>
            )}

            {/* Key properties. */}
            {properties.length > 0 && (
              <section className="rounded-xl border border-border bg-card p-4">
                <h2 className="font-heading text-sm font-semibold">Key facts</h2>
                <dl className="mt-2 space-y-2">
                  {properties.map(([k, v]) => (
                    <div key={k} className="flex flex-col gap-0.5">
                      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground/70">{k}</dt>
                      <dd className="text-sm text-foreground">{v}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}

            {/* Relationships (high-confidence only). */}
            <section className="rounded-xl border border-border bg-card p-4">
              <div className="mb-2 flex items-center gap-2">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Share2 className="h-4 w-4" aria-hidden="true" />
                </span>
                <h2 className="font-heading text-sm font-semibold">
                  Relationships
                  {relationships.length > 0 ? ` (${relationships.length})` : ''}
                </h2>
              </div>
              {relationships.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No high-confidence relationships for this entity.
                </p>
              ) : (
                <ul className="divide-y divide-border/60">
                  {relationships.map((r, i) => {
                    const out = r.direction === 'out';
                    const edge = r.edge_type ? humanise(r.edge_type) : 'related to';
                    const pct = confidencePct(r.confidence);
                    return (
                      <li
                        key={`${r.neighbour_label ?? 'rel'}-${r.edge_type ?? ''}-${i}`}
                        className="flex items-start gap-2 py-2.5"
                      >
                        {out ? (
                          <ArrowRight
                            className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                            aria-label="Outgoing relationship"
                          />
                        ) : (
                          <ArrowLeft
                            className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                            aria-label="Incoming relationship"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground">
                            {r.neighbour_label ?? 'Unknown entity'}
                          </p>
                          <p className="mt-0.5 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                            {out ? '' : '← '}
                            {edge}
                            {pct ? ` · ${pct}` : ''}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </>
        ) : (
          // enabled true but first paint before data — quiet shell.
          <section className="rounded-xl border border-border bg-card p-4">
            <p className="text-sm text-muted-foreground">Loading…</p>
          </section>
        )}
      </div>
    </div>
  );
}
